import React, { useCallback } from 'react';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Spacer from 'design-system/Spacer';
import Button from 'design-system/Button';
import Links from 'utils/links';
import Amplitude from 'utils/amplitude';
import useOldFetch from 'hooks/useFetch';
import { useAppDispatch } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import * as invest from '../types';
import * as actions from '../actions';
import { createOrder } from '../api/orders';
import { amplitudeSource } from '../utils/analytics';
import { ORDER_UPDATE_TIME_LIMIT } from '../constants';
import OrderConfirmView from '../components/OrderConfirmView';
import OrderSendingView from '../components/OrderSendingView';
import { transformAxiosErrorToFailureApiPayload } from '../utils/api';
const strings = {
    failedToExecute: 'Failed to execute the order.',
};
const handlePatternDayTradingLink = () => {
    Links.openKnownLink('patternDayTrading');
};
const useOrderConfirm = (symbol, name, assetId, tradingAccountId, isAlpacaAccount, accountType, operation) => {
    const [, , , fetchOrder] = useOldFetch();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const confirmCallback = useCallback((parameters, cachedOrderPreview, onConfirm) => {
        if (tradingAccountId) {
            if (isAlpacaAccount) {
                const content = parameters.operation === 'buy' ? (<OrderConfirmView buy name={name} symbol={symbol} assetId={assetId} onConfirm={onConfirm} amount={parameters.amount} isAlpacaAccount={isAlpacaAccount} tradingAccountId={tradingAccountId} cachedOrderPreview={cachedOrderPreview}/>) : (<OrderConfirmView sell name={name} symbol={symbol} assetId={assetId} onConfirm={onConfirm} quantity={parameters.quantity} isAlpacaAccount={isAlpacaAccount} tradingAccountId={tradingAccountId} cachedOrderPreview={cachedOrderPreview}/>);
                Modal.show(content);
            }
            else {
                navigation.navigateDeprecated('SecclOrderPreviewScreen', {
                    assetId,
                    orderType: parameters.operation === 'buy'
                        ? { type: 'buy', amount: parameters.amount }
                        : { type: 'sell', quantity: parameters.quantity },
                    onConfirm,
                    tradingAccountId,
                    accountType,
                    orderPreview: cachedOrderPreview,
                });
                return;
            }
            Amplitude.logEvent('Open Order Preview', { source: amplitudeSource, type: parameters.operation, symbol });
        }
    }, [assetId, isAlpacaAccount, name, symbol, tradingAccountId, accountType]);
    const updateStockData = useCallback(() => {
        if (tradingAccountId) {
            if (isAlpacaAccount) {
                dispatch(actions.getActivities({ accountId: tradingAccountId, paging: { page: 1, perPage: 30 } }));
                dispatch(actions.getActivities({
                    accountId: tradingAccountId,
                    assetIds: [assetId],
                    paging: { page: 1, perPage: 30 },
                }));
                dispatch(actions.getPositions({ accountId: tradingAccountId, paging: { page: 1, perPage: 10 ** 6 } }));
                // Refreshing market orders for symbol as well as the main list
                dispatch(actions.getOrders({
                    accountId: tradingAccountId,
                    statuses: ['queued', 'pending'],
                    orderTypes: [invest.SellOrderType.MARKET],
                }));
                dispatch(actions.getOrders({
                    assetIds: [assetId],
                    accountId: tradingAccountId,
                    statuses: ['queued', 'pending'],
                    orderTypes: [invest.SellOrderType.MARKET],
                }));
            }
            else {
                dispatch(actions.fetchTradingAccountInfoAfterOrder(2, tradingAccountId, assetId));
            }
        }
    }, [assetId, isAlpacaAccount, tradingAccountId]);
    /**
     * Throws if pin is not validated
     */
    const executeCallback = useCallback(async (orderPreview, onSuccess, pin) => {
        if (tradingAccountId) {
            const content = <OrderSendingView />;
            Modal.show(content, {
                top: true,
                fullscreen: true,
                nonDismissable: true,
                minShowTime: 1500,
            });
            const { estimated: { amount, quantity }, } = orderPreview;
            try {
                const result = await createOrder(operation === 'buy' ? amount.value : quantity, assetId, tradingAccountId, operation, pin);
                if (result) {
                    dispatch(actions.cacheOrder(result, tradingAccountId));
                    const { order, marketOpeningInfo } = result;
                    const { id, status } = order;
                    if (isAlpacaAccount && status === 'pending' && marketOpeningInfo.status === 'open') {
                        // try to update order for 10 seconds if it’s pending during market opening hours
                        const startTime = new Date().getTime();
                        let currentOrder = order;
                        /* eslint-disable no-await-in-loop */
                        // await inside of loop is exactly what we want here in this case
                        while (currentOrder.status === 'pending' && new Date().getTime() - startTime < ORDER_UPDATE_TIME_LIMIT) {
                            await new Promise((resolve) => {
                                setTimeout(() => {
                                    resolve(true);
                                }, 1000);
                            });
                            try {
                                const orderResult = await fetchOrder(actions.getOrder(id, tradingAccountId));
                                if (orderResult) {
                                    currentOrder = orderResult.order;
                                }
                            }
                            catch {
                                // it will just retry the request in this while loop
                            }
                        }
                        /* eslint-enable no-await-in-loop */
                        dispatch(actions.updateWealthAccounts());
                        updateStockData();
                        Modal.hide(() => {
                            onSuccess(currentOrder, marketOpeningInfo);
                        }, { top: true });
                    }
                    else {
                        dispatch(actions.updateWealthAccounts());
                        updateStockData();
                        Modal.hide(() => {
                            onSuccess(order, marketOpeningInfo);
                        }, { top: true });
                    }
                }
                else {
                    throw new Error();
                }
            }
            catch (error) {
                const axiosErr = error;
                const dispatchProps = transformAxiosErrorToFailureApiPayload(axiosErr);
                if (dispatchProps) {
                    dispatch({
                        type: 'CREATE_ORDER_FAILURE',
                        ...dispatchProps,
                    });
                }
                let message = strings.failedToExecute;
                if (axiosErr?.response?.data?.error?.name === 'PatternDayTradingProtectionError') {
                    message =
                        'This order has failed due to Pattern Day Trade Protection. This is a regulatory requirement, please visit our FAQ for more information.';
                    Modal.hide(() => {
                        Modal.show(<>
                    <Spacer h={8}/>
                    <Text TextThin-14 Gray centered>
                      {message}{' '}
                      <Text Text-14 Primary suppressHighlighting onPress={handlePatternDayTradingLink}>
                        Learn more
                      </Text>
                    </Text>
                    <Spacer h={40}/>
                    <Button brandReversed title="OK, thanks" onPress={Modal.hide}/>
                  </>, {
                            title: 'Error',
                        });
                    }, { top: true });
                    return;
                }
                if (axiosErr?.response?.data?.user_message) {
                    message = axiosErr.response.data.user_message;
                }
                else if ('message' in error && error.message) {
                    message = error.message;
                }
                // failed to execute an order
                if (message === 'Refresh token expired') {
                    Modal.hide(undefined, { top: true });
                    throw error;
                }
                else {
                    Modal.hide(() => {
                        Modal.showError(message);
                    }, { top: true });
                }
            }
        }
    }, [tradingAccountId, operation, assetId, isAlpacaAccount, updateStockData, fetchOrder]);
    return [confirmCallback, executeCallback];
};
export default useOrderConfirm;
